import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Tabs,
  Tab,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/LanguageSwitcher';

export default function Login() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const { login, register, loginWithGoogle, loginWithMicrosoft, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password confirmation and terms for registration
    if (tab === 1) {
      if (password !== confirmPassword) {
        setError(t('register.passwords_not_match'));
        return;
      }
      if (!termsAccepted) {
        setError(t('registration.terms_required'));
        return;
      }
    }

    setLoading(true);

    const result = tab === 0
      ? await login(email, password)
      : await register(email, password);

    setLoading(false);

    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    const result = await loginWithGoogle();
    setLoading(false);

    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setLoading(true);

    const result = await loginWithMicrosoft();
    setLoading(false);

    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleOpenResetDialog = () => {
    setResetEmail(email); // Pre-fill with login email if available
    setResetError('');
    setResetSuccess(false);
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetError(t('login.email_required'));
      return;
    }

    setResetLoading(true);
    setResetError('');

    const result = await resetPassword(resetEmail);
    setResetLoading(false);

    if (result.success) {
      setResetSuccess(true);
    } else {
      setResetError(result.error);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: { xs: 2, sm: 4, md: 8 }, px: { xs: 2, sm: 0 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Viilu Talous
            </Typography>
            <LanguageSwitcher />
          </Box>

          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{ mb: 3 }}
            variant="fullWidth"
          >
            <Tab label={t('login.title')} />
            <Tab label={t('login.register')} />
          </Tabs>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Email/Password Authentication */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            {tab === 1 && (
              <>
                <TextField
                  fullWidth
                  label={t('register.confirm_password')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('registration.terms_accept')}{' '}
                      <Link href="/terms" target="_blank" rel="noopener">
                        {t('registration.terms_link')}
                      </Link>
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={loading || (tab === 1 && !termsAccepted)}
            >
              {tab === 0 ? t('login.button') : t('register.button')}
            </Button>
            {tab === 0 && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleOpenResetDialog}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('login.forgot_password')}
                </Link>
              </Box>
            )}
          </form>

          {/* OAuth Authentication */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {tab === 0 ? 'Sign in with Google' : 'Sign up with Google'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<MicrosoftIcon />}
            onClick={handleMicrosoftLogin}
            disabled={loading}
          >
            {tab === 0 ? 'Sign in with Microsoft' : 'Sign up with Microsoft'}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
            {t('login.powered_by')} Firebase Authentication
          </Typography>
        </Paper>
      </Box>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('login.reset_password_title')}</DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              {t('login.reset_password_sent')}
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
                {t('login.reset_password_instructions')}
              </Typography>
              {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
              <TextField
                fullWidth
                label={t('login.email')}
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={resetLoading}
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>
            {resetSuccess ? t('common.close') : t('common.cancel')}
          </Button>
          {!resetSuccess && (
            <Button onClick={handleResetPassword} variant="contained" disabled={resetLoading || !resetEmail}>
              {t('login.send_reset_link')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
